/**
 * Order Status Screen
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import 'url-search-params-polyfill';

import DesktopPaymentModal from '@/components/DesktopPaymentModal';
import OrderStatusLoader from '@/components/OrderStatusLoader';
import B2bRfqDetailField from './components/B2bRfqDetailField';
import CancelOrderModal from './components/CancelOrderModal';
import MobileCancelBottomModal from './components/MobileCancelBottomModal';

import {
  emptyCart,
  fetchLoggedInOrderByHash,
  getGstInvoice,
  saveCart,
  setLoaderState,
  setPaymentModalVisiblity,
  getCustomerReviewSettings,
  fetchOrderDetailByID,
} from '@/redux/actions';

import { PATH, getRoute } from '@/utils/routes';
import { deviceWidth } from '@/utils/deviceWidth';
import { isOrderPending } from '@/utils/isOrderPending';
import { IS_MOBILE } from '@/utils/checkClientDeviceType';
import { getOrderStatusTextPrefixByStoreType } from '@/utils/getOrderStatusTextPrefixByStoreType';
import { orderItemsToBuyAgain } from '@/utils/orderItemsToBuyAgain';
import { isOrderGstExcluded, isOrderGstIncluded } from '@/utils/orderGSTStatus';
import { DownloadInvoiceIcon } from '@/assets/svgExports/DownloadInvoiceIcon';
import { PhoneIcon } from '@/assets/svgExports/PhoneIcon';
import { WhatsAppIcon } from '@/assets/svgExports/WhatsAppIcon';
import { roundnessCalculator } from '@/components/DesignSystem/AtomicComponents/util';
import { getThemeComponent } from '@/utils/themeImports';
import {
  ORDER_TYPE,
  ORDER_STATUS,
  DISPLAY_STATUS,
  MAX_MOBILE_WIDTH,
  ORDER_GST_TYPE,
  COD_CHARGE_TYPE,
  PREPAID_FLAG,
} from '@/utils/constants';
import getCustomCtaText from '@/utils/getCustomCtaText';
import {
  DownloadInvoiceAnchor,
  DownloadInvoiceContainer,
  DownloadInvoiceText,
} from './OrderStatus.styles';
import moment from 'moment';
import useIsComponentMounted from '@/utils/useIsComponentMounted';
import { CURRENCY_SYMBOL } from '../../../src/config';
import ReviewOrderItems from './ReviewOrderItems';
import { useFeatureLocks } from '@/hooks/useFeatureLocks';
import { getTotalCartAmounts } from '@/utils/getTotalCartAmounts';
import { getDeliveryTimeText } from '@/utils/getDeliveryTimeText';
import { specificThemeRender } from '@/utils/specificThemeRender';
import { useSSRSelector } from '../../redux';
import ReadMoreReadLess from '../../components/ProductCustomizationPopUp/components/ReadMoreReadLess';
import {
  pushPurchaseEventToDataLayer,
  removeExistingPurchaseEventFromDataLayer,
  shouldTrrigerPurchaseEvent,
  unsetPurchaseTrigger,
} from '@/utils/purchaseEventTrigger';
import ItemSchedulingInfo from 'src/components/ItemSchedulingInfo';
import useStoreContactDetails from 'src/hooks/useStoreContactDetails';
import { getWhatAppRedirectionUrl } from 'src/utils/getWhatsAppRedirectionUrl';
const AuthModal = dynamic(() => import('@/components/DesignSystem/AuthPopUp'));

/**
 * Helper to transform orderDetails into Meta Pixel Purchase event parameters.
 */
const preparePurchaseDataForConversion = (orderDetails, storeInfo) => {
  const currency = storeInfo?.currency?.currency_symbol || 'INR';
  let value = 0;
  let numItems = 0;

  const content_ids = [];
  const contents = [];
  orderDetails?.items?.forEach((item) => {
    if (item.item_status === 1) {
      const skuId = (item.id || item.item_id)?.toString();
      const itemPrice = item.price || item.display_price || 0;
      const quantity = item.quantity;

      if (skuId && quantity > 0) {
        content_ids.push(skuId);
        contents.push({
          id: skuId,
          quantity: quantity,
          item_price: itemPrice,
        });
        numItems += quantity;
      }
    }
  });

  value = orderDetails?.buyer_pay_value || orderDetails?.amount || 0;

  return {
    content_ids: content_ids,
    contents: contents,
    content_type: 'product',
    currency: currency,
    value: value,
    num_items: numItems,
    order_id: orderDetails?.order_id?.toString() || '',
  };
};
const OrderStatus = () => {
  const storeInfo = useSSRSelector((state) => state.storeReducer.store);
  const router = useRouter();
  const dispatch = useDispatch();
  const orderHashFromParams = router.query.hash;

  /** Local state */
  const [state, setState] = useState({
    showOrderInstructionModal: false,
    deliveryTracking: {
      text: '',
      isInfoRequired: true,
    },
    pageTheme: null,
    isStatusBlockVisible: false,
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  /** Store Selectors */
  const cart = useSelector((state) => state.cartReducer);
  const { orderDetails, gstInvoice, user, reviewSettings, parentOrderDetails } =
    useSelector((state) => ({
      orderDetails: state.orderReducer?.orderDetails,
      gstInvoice: state.orderReducer?.gstInvoice,
      user: state.userReducer,
      reviewSettings: state.catalogReducer.reviewSettings,
      parentOrderDetails: state.orderReducer?.parentOrderDetails,
    }));
  const { loader } = useSelector((state) => state.commonReducer);
  const userData = useSelector((state) => state.userReducer.data);
  const { services } = useSelector((state) => state.storeReducer.store);
  const isComponentMounted = useIsComponentMounted();
  const { crrFeatureStatus } = useFeatureLocks();
  const isCRRFeatureUnlocked = !crrFeatureStatus?.isLocked;
  const isB2bTypeOrder = orderDetails?.order_type === ORDER_TYPE.B2B;
  const { storePhone } = useStoreContactDetails();

  useEffect(() => {
    if (
      shouldTrrigerPurchaseEvent() &&
      orderDetails?.order_hash === orderHashFromParams
    ) {
      unsetPurchaseTrigger();
      const data = {
        event: 'purchase',
        transaction_id: orderDetails?.order_id || '',
        value: orderDetails?.buyer_pay_value || orderDetails?.amount,
        tax: orderDetails?.total_gst_paid,
        shipping: orderDetails?.delivery_charge,
        currency: 'INR',
      };
      pushPurchaseEventToDataLayer(data, orderDetails?.order_id);

      // --- Meta Pixel Purchase Implementation ---
      const pixelData = preparePurchaseDataForConversion(orderDetails, storeInfo);

      if (window.fbq && pixelData.contents.length > 0) {
        console.log('===Meta Pixel - Purchase Fired:', pixelData);
        window.fbq('track', 'Purchase', pixelData);
      }
    }
    return () => removeExistingPurchaseEventFromDataLayer(orderDetails?.order_id);
  }, [orderDetails?.order_id]);

  useEffect(() => {
    localStorage.removeItem('state');
    localStorage.removeItem('deliveryType');

    dispatch(setLoaderState(true));
    dispatch(emptyCart());
    triggerGetOrderByHash();
    triggerGetOrderByParentId();
    if (storeInfo?.store_id) {
      dispatch(getCustomerReviewSettings(storeInfo.store_id));
    }
    // dispatch(fetchStoreInfoBySubDomain(storeInfo?.domain));

    const intervalTimer = setInterval(() => {
      triggerGetOrderByHash(stopInterval);
    }, 3000);

    if (!localStorage?.getItem('auth_token')) {
      localStorage.removeItem('auth_token');
      setShowAuthModal(true);
    }

    const stopInterval = () => {
      clearInterval(intervalTimer);
    };
    return stopInterval;
  }, []);

  useEffect(() => {
    triggerGetOrderByHash();
  }, [user?.isLogin]);

  useEffect(() => {
    if (orderDetails?.order_id && orderDetails?.order_hash === orderHashFromParams) {
      triggerGetOrderByParentId();
      if (orderDetails?.created_at !== orderDetails.updated_at) {
        const deliveryTracking = getDeliveryTimeText(
          orderDetails?.delivery_info?.delivery_from,
          orderDetails?.delivery_info?.delivery_to,
          orderDetails?.delivery_info?.delivery_time,
          orderDetails?.updated_at,
          orderDetails?.delivery_info?.assigned_to
        );
        setState((state) => ({ ...state, deliveryTracking }));
      }
    }
  }, [orderDetails]);

  useEffect(() => {
    if (
      shouldTrrigerPurchaseEvent() &&
      orderDetails?.order_hash === orderHashFromParams
    ) {
      unsetPurchaseTrigger();
      const data = {
        event: 'purchase',
        transaction_id: orderDetails?.order_id || '',
        value: orderDetails?.buyer_pay_value || orderDetails?.amount,
        tax: orderDetails?.total_gst_paid,
        shipping: orderDetails?.delivery_charge,
        currency: 'INR',
      };
      pushPurchaseEventToDataLayer(data, orderDetails?.order_id);
    }
    return () => removeExistingPurchaseEventFromDataLayer(orderDetails?.order_id);
  }, [orderDetails?.order_id]);

  /** fetching GST invoice  */
  useEffect(() => {
    dispatch(getGstInvoice());
  }, [orderDetails?.prepaid_flag, orderDetails?.status]);

  /**
   * Buy Again handle
   * @param {Array} items
   */
  const handleNavigateReorder = () => {
    if (!isThisCfeSideOrder()) {
      router.push(getRoute(PATH.PRODUCT, storeInfo.store_info.domain));
      return;
    }

    let items = orderDetails.items ? [...orderDetails.items] : [];
    items = orderItemsToBuyAgain(items);
    const amount_values = getTotalCartAmounts(items);
    const payload = {
      items: [...cart.items, ...items],
      store_id: storeInfo?.store_id,
      merchant_id: 1,
      order_type: ORDER_TYPE.PICKUP,
      phone: userData?.phone,
      status: 1,
      status_message: 'New',
      deliver_to: '',
      address1: '',
      address2: '',
      latitude: 0,
      longitude: 0,
      ...amount_values,
    };

    dispatch(saveCart(payload));
    router.push(getRoute(PATH.PRODUCT, storeInfo?.store_info?.domain));
  };

  function triggerGetOrderByParentId() {
    if (orderDetails?.parent_order_hash && orderDetails.parent_order_hash !== '') {
      dispatch(fetchOrderDetailByID({ order_hash: orderDetails?.parent_order_hash }));
    }
  }

  function isExtraCODCHargeFeatureActive() {
    return storeInfo?.services?.cod_charge_flag === COD_CHARGE_TYPE.FREE;
  }

  /** Calls getorderbyhash api if orderHashParams are present*/
  function triggerGetOrderByHash(errorCallback) {
    orderHashFromParams &&
      user?.isLogin &&
      dispatch(
        fetchLoggedInOrderByHash(
          {
            order_hash: orderHashFromParams,
            phone: userData?.phone,
            storeId: storeInfo?.store_id,
          },
          errorCallback
        )
      );
  }
  /** Pay Now button handle */
  const onPayNowBtnClick = () => {
    // for desktop user
    if (deviceWidth > 990) {
      dispatch(setPaymentModalVisiblity(true));
    }
    // for mobile user
    else {
      router.push(
        getRoute(PATH.payment(orderDetails?.order_hash), storeInfo?.store_info?.domain)
      );
    }
  };

  const isThisCfeSideOrder = () => {
    return [0, 1, 2].includes(orderDetails?.order_type);
  };

  const toggleStatusBlock = () => {
    setState((state) => ({
      ...state,
      isStatusBlockVisible: !state.isStatusBlockVisible,
    }));
  };

  /**
   * If order is of partial payment type, then if the order is complete - it should return true, else false.
   * If order is of normal type, i.e non partial payment type, then this function will have no impact and returns true.
   * @return {boolean}
   */
  const isOrderPartialPaymentTypeAndComplete = () => {
    if (orderDetails?.part_payment_info && orderDetails?.partial_payment_type > 0) {
      return orderDetails?.part_payment_info?.order_completion_status === 3;
    }
    return true;
  };

  /**
   * Helper function for calculation of
   * item amount total
   * @return {number}
   */
  const getItemTotal = () => {
    return orderDetails?.items?.reduce((acc, curr) => {
      if (curr.item_status === 1) acc = acc + curr.amount;

      return acc;
    }, 0);
  };

  /**
   * return the order process status text
   * @return {string}
   */
  const getOrderStatusProcessText = () => {
    const processingText = {
      title: '',
      subtitle: '',
    };

    if (
      orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
      orderDetails?.status === ORDER_STATUS.CONFIRMED_COD
    ) {
      processingText.title = isB2bTypeOrder ? 'Request Raised' : 'Order Placed';
      processingText.subtitle = isB2bTypeOrder
        ? 'We have received your request.'
        : 'We have received your order.';
    } else if (
      orderDetails?.status === ORDER_STATUS.ORDER_SEEN &&
      orderDetails?.digital_receipt === ''
    ) {
      processingText.title = isB2bTypeOrder ? 'Request Seen' : 'Order Seen';
      processingText.subtitle = 'We will get your order ready for pickup soon.';
    } else {
      if (orderDetails?.order_type == ORDER_TYPE.PICKUP) {
        processingText.title = 'Your Order is Ready for Pickup';
      } else
        processingText.title = isB2bTypeOrder
          ? 'Your quotation has been Verified successfully'
          : 'Your Order is Verified Successfully';
    }

    return processingText;
  };

  const getOrderStatusProcessTextServiceBased = () => {
    const processingText = {
      title: '',
      subtitle: '',
    };

    if (orderDetails?.status === ORDER_STATUS.ORDER_PLACED) {
      processingText.title = 'Booking Placed';
      processingText.subtitle = 'We have received your booking.';
    } else {
      processingText.title = 'Booking Seen';
      processingText.subtitle = 'We will get your booking ready soon.';
    }

    return processingText;
  };

  const toggleAuthModal = () => {
    if (!localStorage?.getItem('auth_token')) return;
    if (user?.isLogin && showAuthModal) {
      setShowAuthModal(false);
    } else if (user?.isLogin && !showAuthModal) {
      setShowAuthModal(false);
    } else {
      setShowAuthModal((prevState) => !prevState);
    }
  };

  function renderDownloadInvoice() {
    return gstInvoice ? (
      <DownloadInvoiceAnchor
        href={orderDetails?.gst_invoice}
        target="_blank"
        download="invoice.pdf"
      >
        <DownloadInvoiceContainer
          roundness={roundnessCalculator(storeInfo?.theme?.roundness)}
        >
          <DownloadInvoiceIcon />
          <DownloadInvoiceText>Download Invoice</DownloadInvoiceText>
        </DownloadInvoiceContainer>
      </DownloadInvoiceAnchor>
    ) : null;
  }
  /**
   * Return the boolean for COD type payments
   * @return {boolean}
   */
  const isPaymentTypeIsCOD = () =>
    (orderDetails?.delivery_info?.assigned_to == 1 &&
      orderDetails?.payment_status == 1) ||
    (storeInfo?.services?.cod_charge_flag > COD_CHARGE_TYPE.FREE &&
      orderDetails?.prepaid_flag === 0);

  const isPrepaidOrderCancelledOrRejected = () => {
    return (
      (orderDetails?.status === ORDER_STATUS.REJECTED ||
        orderDetails?.status === ORDER_STATUS.CUSTOMER_CANCELLED) &&
      orderDetails?.payment_status == 9
    );
  };

  /**
   * Return JSX for
   * Order Status type Complete
   * @return {JSX}
   */
  const _renderOrderStatusComplete = () => {
    return (
      <>
        {orderDetails?.status === ORDER_STATUS.COMPLETED ? (
          <article className="tc fw6 flex justify-start items-center">
            {orderDetails?.prepaid_flag !== 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
            Your Order is Complete.
            {orderDetails?.prepaid_flag === 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
          </article>
        ) : orderDetails?.status === ORDER_STATUS.REJECTED &&
          orderDetails?.display_status === DISPLAY_STATUS.REJECTED ? (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/eJFFlBiC.webp"
              className="mb2 img h2"
            />
            <br></br>
            Your order has been rejected
            <br></br>
            {orderDetails?.status_message && (
              <span className="f7 fw6 black-40">{orderDetails?.status_message}</span>
            )}
            {isPrepaidOrderCancelledOrRejected() && (
              <p className="f7 fw6 black-40">{`The merchant has rejected your order. Please contact them at ${storePhone} for refund or further assistance.`}</p>
            )}
          </article>
        ) : orderDetails?.status === ORDER_STATUS.CUSTOMER_CANCELLED ? (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/eJFFlBiC.webp"
              className="mb2 img h2"
            />
            <br></br>
            {getOrderStatusTextPrefixByStoreType()} Cancelled
            <br></br>
            {orderDetails?.status_message && (
              <span className="f7 fw6 black-40">{orderDetails?.status_message}</span>
            )}
            {isPrepaidOrderCancelledOrRejected() && (
              <p className="f7 fw6 black-40">{`Please contact the merchant at ${storePhone} for assistance. The refund will be processed manually.`}</p>
            )}
          </article>
        ) : (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/KIE24hYi.webp"
              className="mb2 img h2"
            />
            <br></br>
            {getOrderStatusProcessText()?.title}
            <br></br>
            {orderDetails?.order_type === ORDER_TYPE.DELIVERY &&
              state.deliveryTracking.text && (
                <span className="f7 fw6 black-40">{state.deliveryTracking.text}</span>
              )}
          </article>
        )}
      </>
    );
  };

  const _renderOrderStatusCompleteService = () => {
    return (
      <>
        {orderDetails?.status === ORDER_STATUS.COMPLETED ? (
          <article className="tc fw6 flex justify-start items-center">
            {orderDetails?.prepaid_flag !== 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
            Your Booking is Complete.
            {orderDetails?.prepaid_flag === 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
          </article>
        ) : orderDetails?.status === ORDER_STATUS.REJECTED &&
          orderDetails?.display_status === DISPLAY_STATUS.REJECTED ? (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/eJFFlBiC.webp"
              className="mb2 img h2"
            />
            <br></br>
            Your Booking has been rejected
            <br></br>
            {orderDetails?.status_message && (
              <span className="f7 fw6 black-40">{orderDetails?.status_message}</span>
            )}
          </article>
        ) : orderDetails?.status === ORDER_STATUS.CUSTOMER_CANCELLED ? (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/eJFFlBiC.webp"
              className="mb2 img h2"
            />
            <br></br>
            Booking Cancelled
            <br></br>
            {orderDetails?.status_message && (
              <span className="f7 fw6 black-40">{orderDetails?.status_message}</span>
            )}
          </article>
        ) : (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/KIE24hYi.webp"
              className="mb2 img h2"
            />
            <br></br>
            {getOrderStatusProcessTextServiceBased()?.title}
            <br></br>
          </article>
        )}
      </>
    );
  };

  /**
   * Return JSX for
   * Order Status Processing
   * @return {JSX}
   */
  const _renderOrderStatusProccessingService = () => {
    return (
      <section className="order-status__processing">
        <div className="order-status__processing--top">
          <article className="title-text">
            <span className="f5 fw6">
              {getOrderStatusProcessTextServiceBased()?.title}
            </span>
            <br />
            <span className="f7 fw5 gray">
              {getOrderStatusProcessTextServiceBased()?.subtitle}
            </span>
          </article>
          <article className="status">
            <div
              className="f7 fw6 pv2 mr2 flex justify-start items-center pointer"
              onClick={toggleStatusBlock}
            >
              Check Status{' '}
              <img
                alt="dropdown button"
                src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
                className={`h2 img ml2 ${
                  state.isStatusBlockVisible ? 'arrow-up' : 'arrow-down'
                }`}
              />
            </div>
          </article>
        </div>
        {state.isStatusBlockVisible && (
          <div className="order-status__processing--bottom pt4 pb3 ph1 flex justify-between">
            <article className="flex flex-column items-center f7 fw6 mw4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              {'Booking Placed'}
            </article>
            <article className="flex flex-column justify-center items-center tc f7 fw6 mw4 h-100">
              {orderDetails?.status === ORDER_STATUS.ORDER_SEEN ? (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              ) : (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              )}
              <br />
              {'Booking Seen'}
              {orderDetails?.status === ORDER_STATUS.ORDER_PLACED && (
                <span className="o-30 f8 mt2">{`Booking is yet to be seen.`}</span>
              )}
            </article>
            <article className="flex flex-column items-center  f7 fw6 m4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              {'Booking Processed'}
            </article>
          </div>
        )}
      </section>
    );
  };

  const _renderOrderStatusProccessing = () => {
    return (
      <section className="order-status__processing">
        <div className="order-status__processing--top">
          <article className="title-text">
            <span className="f5 fw6">{getOrderStatusProcessText()?.title}</span>
            <br />
            <span className="f7 fw5 gray">{getOrderStatusProcessText()?.subtitle}</span>
          </article>
          <article className="status">
            <div
              className="f7 fw6 pv2 mr2 flex justify-start items-center pointer"
              onClick={toggleStatusBlock}
            >
              Check Status{' '}
              <img
                alt="dropdown button"
                src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
                className={`h2 img ml2 ${
                  state.isStatusBlockVisible ? 'arrow-up' : 'arrow-down'
                }`}
              />
            </div>
          </article>
        </div>
        {state.isStatusBlockVisible && (
          <div className="order-status__processing--bottom pt4 pb3 ph1 flex justify-between">
            <article className="flex flex-column items-center f7 fw6 mw4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              {isB2bTypeOrder ? 'Request Raised' : 'Order Placed'}
            </article>
            <article className="flex flex-column justify-center items-center tc f7 fw6 mw4 h-100">
              {orderDetails?.status === ORDER_STATUS.ORDER_SEEN ? (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              ) : (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              )}
              <br />
              {isB2bTypeOrder ? 'Request Seen' : 'Order Seen'}
              {orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
                (orderDetails?.status === ORDER_STATUS.CONFIRMED_COD && (
                  <span className="o-30 f8 mt2">{`${
                    isB2bTypeOrder ? 'Request' : 'Order'
                  } is yet to be seen.`}</span>
                ))}
            </article>
            <article className="flex flex-column items-center  f7 fw6 m4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              {orderDetails?.order_type === ORDER_TYPE.PICKUP ? (
                <>
                  Ready for <br />
                  Pickup{' '}
                </>
              ) : isB2bTypeOrder ? (
                'Request Closed'
              ) : (
                <>
                  Out for <br /> Delivery
                </>
              )}
            </article>
          </div>
        )}
      </section>
    );
  };

  /**
   * Return JSX for
   * Order Status Block
   * @return {JSX}
   */
  const _renderOrderStatus = () => {
    return (
      <div className="order-status">
        {!(
          orderDetails?.status === ORDER_STATUS.ORDER_SEEN ||
          orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
          orderDetails?.status === ORDER_STATUS.CONFIRMED_COD
        )
          ? _renderOrderStatusComplete()
          : _renderOrderStatusProccessing()}
      </div>
    );
  };

  const _renderOrderStatusService = () => {
    return (
      <div className="order-status">
        {!(
          orderDetails?.status === ORDER_STATUS.ORDER_SEEN ||
          orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
          orderDetails?.status === ORDER_STATUS.CONFIRMED_COD
        )
          ? _renderOrderStatusCompleteService()
          : _renderOrderStatusProccessingService()}
      </div>
    );
  };

  const _renderOrderStatusforAppSideOrders = () => {
    return !isOrderPending(orderDetails?.display_status) &&
      orderDetails?.display_status !== DISPLAY_STATUS.REJECTED ? (
      <div className="order-status">
        <article className="tc fw6 mb2">
          <img
            src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
            className="mb2 img h3"
          />
          <br></br>
          Your have paid ₹{orderDetails.pay_amount}
        </article>
      </div>
    ) : null;
  };

  function renderOrderTime() {
    return (
      <span className="order-time star-dust f12px fw4 mt8px">
        {isB2bTypeOrder ? 'Raised' : 'Placed'} on{' '}
        {orderDetails?.created_at?.split(' ')[0]} at{' '}
        {moment(orderDetails?.created_at).format('LT').toLowerCase()}
      </span>
    );
  }
  /**
   * Return JSX for Bill
   * @return {JSX}
   */
  const _renderBill = () => {
    return (
      <section
        className="order-bill"
        style={{
          minHeight: isB2bTypeOrder ? '60px' : '100px',
          padding: isB2bTypeOrder ? '18px 14px 4px 14px' : '18px 14px 20px 14px',
        }}
      >
        <div className="items-details-container tw-flex tw-flex-col tw-gap-4 ">
          {orderDetails?.items?.map((item, ind) => {
            const savingAmt = item?.actual_amount * item?.quantity - item.amount;
            const personalizationData = item?.personalization_info?.personalizations;
            return item.item_status === 1 ? (
              <div
                className={`tw-flex tw-flex-col ${ind !== orderDetails?.items?.length - 1 ? 'tw-border-b-[0.3000000119px] tw-border-dashed tw-border-[#9d9fa9]' : ''}`}
              >
                <article
                  className="bill-table bill-items-margin"
                  key={`order-item-${ind}`}
                >
                  <div
                    className="fw4 item-name-container responsive-major-text"
                    style={{ maxWidth: isB2bTypeOrder ? '100%' : '50%' }}
                  >
                    <div className="flex flex-column tw-text-[0.75rem] tw-font-semibold md:tw-text-[0.875rem] ">
                      {item?.item_name} X {item?.quantity}
                      {item?.variant_name && (
                        <span className="mt4px star-dust responsive-minor-text">
                          {' '}
                          {item?.variant_name}{' '}
                        </span>
                      )}
                    </div>
                    {item.additional_remark ? (
                      <div className="flex flex-column f12px">
                        <p className="mt3 mb0 fw4 tile-gray-color">Additional Remarks:</p>
                        <p className="mt1 fw4 mb0">"{item.additional_remark}"</p>
                      </div>
                    ) : null}
                  </div>
                  {!isB2bTypeOrder ? (
                    <div className="flex flex-column fw4">
                      <span className="flex justify-end">
                        {item?.item_type !== 'list' ? `₹ ${item?.discounted_price}` : '-'}
                      </span>
                      {item?.item_type !== 'list' && savingAmt > 0 && (
                        <span className="fw4 mt4px color-green responsive-minor-text">
                          {CURRENCY_SYMBOL}&nbsp;{savingAmt?.toFixed(2)} Saved
                        </span>
                      )}
                    </div>
                  ) : null}
                  {/* <div className="fw4 f7 tr">₹{item?.amount}</div> */}
                </article>
                <ItemSchedulingInfo
                  schedulingInfo={item?.schedule_info}
                  customClasses="!tw-pt-[10px] !tw-pb-[24px]"
                  headingClasses="!tw-font-medium"
                  paraClasses="!tw-text-[14px]/[17px]"
                />
                {!!personalizationData?.length &&
                  personalizationData?.map((data, idx) => (
                    <div
                      key={idx}
                      className={`tw-mb-[1rem] tw-flex tw-flex-col tw-gap-[0.75rem]`}
                    >
                      <span className="tw-flex tw-gap-[0.5rem]">
                        <span className="tw-text-[0.75rem] tw-font-medium tw-text-[#242121] md:tw-text-[0.875rem]">
                          {' '}
                          Personalisation details{' '}
                        </span>{' '}
                        <span className="tw-text-[0.875rem] tw-font-normal tw-text-[#242121]">
                          x {data?.quantity}
                        </span>
                      </span>
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        {!!data?.images?.length && (
                          <div className="tw-flex tw-flex-wrap tw-gap-[0.625rem]">
                            {data?.images?.map((imgUrl, idx) => (
                              <img
                                src={imgUrl}
                                alt="image"
                                key={idx}
                                width={'40px'}
                                height={'40px'}
                                className="tw-rounded tw-border tw-border-solid tw-border-[#0000001A]"
                              />
                            ))}
                          </div>
                        )}
                        {!!data?.texts?.length && (
                          <div className="tw-flex tw-flex-col tw-gap-2">
                            {data?.texts?.map((text, idx) => (
                              <ReadMoreReadLess text={text} color="#6B6B6B" key={idx} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : null;
          })}
        </div>
        {!isB2bTypeOrder ? (
          <>
            <div className="overall-details-wrapper sonic-silver">
              <article className="bill-table">
                <div className="fw4 f7">
                  {orderDetails?.order_type === ORDER_TYPE.SERVICE
                    ? 'Total'
                    : 'Item Total'}
                </div>
                <div className="fw4 f7 tr">₹&nbsp;{getItemTotal()?.toFixed(2)}</div>
              </article>
              {orderDetails?.order_type === ORDER_TYPE.DELIVERY &&
                Number(orderDetails?.delivery_charge) > 0 && (
                  <article className="bill-table mt10px">
                    <div className="fw4 f7">Delivery fees</div>
                    <div className="fw4 f7 tr">
                      ₹&nbsp;{orderDetails?.delivery_charge}
                    </div>
                  </article>
                )}
              {orderDetails?.order_type === ORDER_TYPE.DELIVERY &&
                (storeInfo?.services?.cod_charge_flag > COD_CHARGE_TYPE.FREE ||
                  orderDetails?.cod_charges > 0) && (
                  <article className="bill-table mt10px">
                    <div className="fw4 f7">Extra Charges on COD</div>
                    <div className="fw4 f7 tr">₹&nbsp;{orderDetails?.cod_charges}</div>
                  </article>
                )}
              {orderDetails?.extra_charges > 0 && (
                <article className="bill-table mt10px">
                  <div className="fw4 f7">
                    {orderDetails?.extra_charge_name.substr(0, 20)}
                    {orderDetails?.extra_charge_name.length > 20 && '...'}
                  </div>
                  <div className="fw4 f7 tr">₹&nbsp;{orderDetails?.extra_charges}</div>
                </article>
              )}
              {orderDetails?.discount > 0 && (
                <article className="bill-table mt10px color-green">
                  <div className="fw4 f7">Discount</div>
                  <div className="fw4 f7 tr">- ₹&nbsp;{orderDetails?.discount}</div>
                </article>
              )}
              {orderDetails?.promo_discount > 0 && (
                <article className="bill-table mt10px color-green">
                  <div className="fw4 f7">Promo Discount</div>
                  <div className="fw4 f7 tr">- ₹&nbsp;{orderDetails?.promo_discount}</div>
                </article>
              )}
              {isOrderGstExcluded() && (
                <article className="bill-table mt10px">
                  <div className="fw4 f7">GST</div>
                  <div className="fw4 f7 tr">₹&nbsp;{orderDetails?.total_gst_paid}</div>
                </article>
              )}
            </div>
            <div className="conclusion">
              <article className="flex justify-between items-center">
                <span className="fw6 responsive-major-text">
                  Grand Total
                  {isOrderGstIncluded() && (
                    <span className="incl-gst responsive-minor-text fw4 ml6px">
                      (Incl. ₹ {orderDetails?.total_gst_paid} GST)
                    </span>
                  )}
                </span>
                <span className="fw6 pay-amount responsive-major-text">
                  ₹&nbsp;
                  {orderDetails?.part_payment_info
                    ? orderDetails?.part_payment_info?.total_payable_amount
                    : orderDetails?.pay_amount}
                </span>
              </article>
              {orderDetails?.offer_text ? (
                <div
                  className="flex justify-start items-center f7 fw4 pv1 mr5"
                  dangerouslySetInnerHTML={{ __html: orderDetails?.offer_text }}
                />
              ) : null}
            </div>
          </>
        ) : null}
      </section>
    );
  };

  const noOrderDetailsFound = () => <p className="tc pt3 f3">No order details found!</p>;

  const _renderPartialPaymentDetail = (orderDetail) => {
    if (orderDetail?.part_payment_info?.order_completion_status === 3) return null;

    const partiallyPaidAmount = orderDetail.part_payment_info?.amount_paid;
    const remainingAmount = orderDetail.part_payment_info?.due_amount;
    const partialPaymentType = storeInfo.services.partial_payment_flag;
    const partialPaymentShare = storeInfo.services.partial_payment_value;
    return (
      <section className="partial-payment-receipt">
        <div className="icons-display">
          <div className="icon one">
            <img src="/assets/icons/greenTickIcon.svg" />
          </div>
          <div className="icon two">
            <img src="/assets/icons/grayPendingIcon.svg" />
          </div>
        </div>
        <div className="amount-display">
          <div className="partial-pay-amount">
            <span className="amount-value">₹ {partiallyPaidAmount}</span>
            <span className="amount-text">
              ({partialPaymentShare}
              {partialPaymentType === 1 ? '%' : '₹'} of grand total)
            </span>
          </div>
          <div className="remaining-pay-amount">
            <span className="amount-value">₹ {remainingAmount}</span>
            <br />
            <span className="amount-text">(Remaining Amount)</span>
          </div>
        </div>
      </section>
    );
  };

  /**
   * return footer segment of page if certain conditions are met
   * conditions are as followed
   * - lodaer is false
   * - store is not closed
   * - order is not pending
   * - order is not rejected
   * @return {JSX} JSX Component
   */
  const footerComponent = () => {
    return (
      loader === false &&
      services?.store_flag !== 0 &&
      !isOrderPending(orderDetails?.display_status) &&
      orderDetails?.display_status !== DISPLAY_STATUS.REJECTED &&
      !showCancellationModal &&
      !isCancelModalOpen && (
        <footer className="ph3 pv2 bg-white flex flex-column justify-center items-center shadow-1 z-2">
          {!isThisCfeSideOrder() && <p className="tc">Looking for something else</p>}
          <button
            className="w-100 pv3 white bold f4 bn"
            style={{
              backgroundColor: storeInfo?.theme?.colors?.primary_color
                ? storeInfo?.theme?.colors?.primary_color
                : '#ea5151',
            }}
            onClick={handleNavigateReorder}
          >
            {isThisCfeSideOrder() ? 'Buy Again' : 'Go to Online Store'}
          </button>
        </footer>
      )
    );
  };

  const handleMoreOptions = () => {
    setShowMoreOptions(!showMoreOptions);
    setIsCancelModalOpen(!isCancelModalOpen);
  };

  const isOrderInInitialisedState = () => {
    if (
      orderDetails?.part_payment_info &&
      orderDetails.part_payment_info?.child_orders.length > 0
    ) {
      const childOrder = orderDetails?.part_payment_info.child_orders[0];
      return [
        ORDER_STATUS.ORDER_PLACED,
        ORDER_STATUS.ORDER_SEEN,
        ORDER_STATUS.CONFIRMED_COD,
        ORDER_STATUS.PART_ORDER_RECIEVED,
      ].includes(childOrder?.status);
    }
    return [
      ORDER_STATUS.ORDER_PLACED,
      ORDER_STATUS.ORDER_SEEN,
      ORDER_STATUS.CONFIRMED_COD,
      ORDER_STATUS.PART_ORDER_RECIEVED,
    ].includes(orderDetails?.status);
  };

  const handleCancelOrder = () => {
    setShowCancellationModal(!showCancellationModal);
  };

  const getCODPaymentMessage = () => {
    if (!isB2bTypeOrder || (isB2bTypeOrder && !!orderDetails?.pay_amount)) {
      return (
        <p
          className="mb0 mt2 tc f12px fw6"
          style={{
            color: storeInfo?.theme?.colors?.primary_color
              ? storeInfo?.theme?.colors?.primary_color
              : '#ea5151',
          }}
        >{`Pay ₹${orderDetails?.pay_amount} to delivery person`}</p>
      );
    }
  };

  const orderDetailComponent = () => {
    return (
      <>
        {orderDetails?.delivery_info?.tracking_url && (
          <section className="flex pa20px">
            <article className="w-60">
              <p className="mb1 f20px mt0">Arriving Soon</p>
              <div className="f12px lh-copy">Track your Shipment now</div>
            </article>
            <article className="flex justify-end items-start w-40">
              <a
                className="pv10px ph24px link"
                style={{
                  color: storeInfo?.theme?.colors?.primary_color
                    ? storeInfo?.theme?.colors?.primary_color
                    : '#ea5151',
                  border: `1px solid ${
                    storeInfo?.theme?.colors?.primary_color
                      ? storeInfo?.theme?.colors?.primary_color
                      : '#ea5151'
                  }`,
                  borderRadius: roundnessCalculator(storeInfo?.theme?.roundness),
                }}
                href={orderDetails?.delivery_info?.tracking_url}
                rel="noreferrer"
                target="_blank"
              >
                Track
              </a>
            </article>
          </section>
        )}
        {/* ORDER STATUS SECTION  */}
        {isThisCfeSideOrder()
          ? _renderOrderStatus()
          : _renderOrderStatusforAppSideOrders()}
        {orderDetails?.part_payment_info &&
        orderDetails?.part_payment_info?.order_completion_status !==
          3 ? null : !isOrderPending(orderDetails?.display_status) &&
          reviewSettings?.status &&
          isCRRFeatureUnlocked &&
          user?.isLogin &&
          orderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          orderDetails?.status !== ORDER_STATUS.REJECTED ? (
          <div
            className="flex items-center justify-between pv16px ph16px pointer"
            onClick={() =>
              document.getElementById('review-section')?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              })
            }
          >
            <p className="mv0 fw5 f14px">Rate the products</p>
            <img
              alt="dropdown button"
              src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
              width="10px"
            />
          </div>
        ) : null}

        {!isThisCfeSideOrder() && _renderOrderStatusService()}
        {showCancellationModal && (
          <CancelOrderModal
            showCancellationModal={showCancellationModal}
            setShowCancellationModal={setShowCancellationModal}
            setShowMoreOptions={setShowMoreOptions}
            setIsCancelModalOpen={setIsCancelModalOpen}
          />
        )}
        {orderDetails?.prepaid_flag === 0 &&
          orderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          orderDetails?.status !== ORDER_STATUS.REJECTED &&
          isOrderPending(orderDetails?.display_status) && (
            <section className="bb b--dashed b--black-20 pa3 pay-now-box">
              <article>
                {isExtraCODCHargeFeatureActive() &&
                  orderDetails?.cod_charges === 0 &&
                  orderDetails?.prepaid_flag == PREPAID_FLAG.POSTPAID && (
                    <button
                      className={`pv3 f5 fw6 white bn w-100 ${
                        (orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
                          orderDetails?.status === ORDER_STATUS.ORDER_SEEN ||
                          isPaymentTypeIsCOD()) &&
                        'o-50'
                      }`}
                      style={{
                        backgroundColor: storeInfo?.theme?.colors?.primary_color
                          ? storeInfo?.theme?.colors?.primary_color
                          : '#ea5151',
                        borderRadius: roundnessCalculator(storeInfo?.theme?.roundness),
                      }}
                      onClick={onPayNowBtnClick}
                      disabled={
                        orderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
                        orderDetails?.status === ORDER_STATUS.ORDER_SEEN ||
                        isPaymentTypeIsCOD()
                      }
                    >
                      Pay Now
                    </button>
                  )}
                {isPaymentTypeIsCOD() && getCODPaymentMessage()}
              </article>
              {isExtraCODCHargeFeatureActive() && isOrderInInitialisedState() && (
                <p className="f7 fw5 black-50">
                  {orderDetails?.order_type === ORDER_TYPE.SERVICE
                    ? 'You can make a payment once you avail the service.'
                    : orderDetails?.order_type === ORDER_TYPE.PICKUP
                      ? 'You can make payment once order is ready for pickup.'
                      : 'You can make payment once order is Verified Successfully.'}
                </p>
              )}
            </section>
          )}

        {isOrderInInitialisedState() &&
          storeInfo?.services?.cancel_flag === 2 &&
          orderDetails.cancellation_deadline !== '' &&
          moment(orderDetails?.cancellation_deadline?.substring(0, 10)).isSameOrAfter(
            moment().format('YYYY-MM-DD')
          ) && (
            <div
              className={`more-options-box ${
                showMoreOptions ? 'all-more-options' : 'single-more-options'
              }`}
            >
              <div
                className="f7 fw6 pv2 mr2 flex justify-start items-center pointer more-options-heading-box"
                onClick={handleMoreOptions}
              >
                <div className="more-options-heading">More Options </div>
                <img
                  alt="dropdown button"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
                  className={` below-arrow ${
                    showMoreOptions && isCancelModalOpen ? 'arrow-up' : 'arrow-down'
                  }`}
                />
              </div>
              {!IS_MOBILE && showMoreOptions && (
                <div className="cancel-order pointer" onClick={handleCancelOrder}>
                  Cancel Order
                </div>
              )}
              {IS_MOBILE && showMoreOptions && isCancelModalOpen && (
                <MobileCancelBottomModal
                  handleCancelOrder={handleCancelOrder}
                  setShowMoreOptions={setShowMoreOptions}
                  showMoreOptions={showMoreOptions}
                  setIsCancelModalOpen={setIsCancelModalOpen}
                />
              )}
            </div>
          )}

        {isOrderPartialPaymentTypeAndComplete() &&
          orderDetails?.status !== ORDER_STATUS.REJECTED &&
          orderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          renderDownloadInvoice()}
        <div className="bill-wrapper">
          <div className="bill-wrapper__orderDetails flex justify-center items-center">
            <article className="fw6 f16px flex flex-column">
              <span className="flex justify-center">
                {getCustomCtaText('bill_order_id')} {orderDetails?.order_id}
              </span>
              {renderOrderTime()}
            </article>
          </div>
        </div>
        {/* DIGITAL BILL  */}
        {_renderBill()}
        {!!storeInfo?.services?.partial_payment_flag &&
          orderDetails.part_payment_info &&
          _renderPartialPaymentDetail(orderDetails)}
        {orderDetails?.part_payment_info &&
        orderDetails?.part_payment_info?.order_completion_status !==
          3 ? null : !isOrderPending(orderDetails?.display_status) &&
          orderDetails?.status !== ORDER_STATUS.REJECTED &&
          orderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          reviewSettings?.status &&
          isCRRFeatureUnlocked &&
          user?.isLogin ? (
          <div id="review-section">
            <ReviewOrderItems
              triggerGetOrderByHash={triggerGetOrderByHash}
              items={orderDetails?.items}
              orderDetails={orderDetails}
            />
          </div>
        ) : null}
      </>
    );
  };

  function renderThankYouHeaderIcon() {
    const orderStatus = (
      isOrderChildPartialPaymentType() ? orderDetails : parentOrderDetails
    )?.status;
    const removeWhatsappMessage = [
      ORDER_STATUS.COMPLETED,
      ORDER_STATUS.REJECTED,
      ORDER_STATUS.CUSTOMER_CANCELLED,
    ].includes(orderStatus);

    const whatsappDefaultMessage = !removeWhatsappMessage
      ? 'Hi%2C+is+my+order+ready%3F'
      : '';

    return (
      <>
        {deviceWidth < MAX_MOBILE_WIDTH && storePhone ? (
          <a
            href={`tel:${storePhone}`}
            style={{
              marginRight: 10,
              padding: '8px 10px',
            }}
          >
            <PhoneIcon color={storeInfo?.theme?.colors?.primary_color} />
          </a>
        ) : null}
        <a
          href={getWhatAppRedirectionUrl(whatsappDefaultMessage)}
          target="_blank"
          style={{
            padding: '1px 5px',
          }}
          rel="noreferrer"
        >
          <WhatsAppIcon />
        </a>
      </>
    );
  }

  function thankYouHeader() {
    return (
      isComponentMounted && (
        <section className="header-mobile">
          <div>
            {deviceWidth < MAX_MOBILE_WIDTH && (
              <article className="header__top fixed w-100 top-0 bg-white z-2 shadow-1">
                <img
                  src="https://cdn.dotpe.in/longtail/themes/8073865/1cy3Zqf6.webp"
                  onClick={() =>
                    router.push(getRoute(PATH.PRODUCT, storeInfo?.store_info?.domain))
                  }
                />
                {getCustomCtaText('bill_order_id')} {orderDetails?.order_id}
              </article>
            )}
            <article className="header--bottom-wrapper">
              <article className="header__bottom">
                <div className="header__bottom--thank">
                  Thank you for<br></br> your{' '}
                  {isB2bTypeOrder
                    ? 'request'
                    : orderDetails?.order_type === ORDER_TYPE.SERVICE
                      ? 'Booking'
                      : 'Order'}{' '}
                  <img src="https://cdn.dotpe.in/longtail/themes/8073865/hhdn1Ncs.webp" />
                </div>
                <div className="header__bottom--actions">
                  {renderThankYouHeaderIcon()}
                </div>
              </article>
            </article>
          </div>
        </section>
      )
    );
  }

  function renderB2bRfqDetails() {
    const rfqDetailsObj = orderDetails?.order_rfq_details;
    const rfqDetailsObjKeys = Object.keys(rfqDetailsObj);
    const filteredRfqDetailsObjKeys = rfqDetailsObjKeys.filter((key) => {
      if (rfqDetailsObj[key]) return key;
    });
    const filteredRfqDetailsObjKeysLength = filteredRfqDetailsObjKeys?.length;

    return (
      !!filteredRfqDetailsObjKeysLength && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '18px 14px 4px 14px',
            margin: '0x 18px',
          }}
        >
          <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#000000' }}>
            Your Details
          </h2>
          <section
            style={{
              width: '100%',
              boxShadow: '0px 1px 3px #00000027',
              borderRadius: 8,
              border: '0.5px solid #e2e2e2',
              margin: '0x 18px',
              padding: '18px 14px 4px 14px',
            }}
          >
            {filteredRfqDetailsObjKeys.map((fieldkey, idx) => (
              <B2bRfqDetailField
                key={idx}
                fieldkey={fieldkey}
                value={rfqDetailsObj[fieldkey]}
                isFirstElement={idx === 0}
                isLastElement={idx === filteredRfqDetailsObjKeysLength - 1}
              />
            ))}
          </section>
        </div>
      )
    );
  }

  function isParentOrderGstIncluded() {
    if (parentOrderDetails?.gst_type === ORDER_GST_TYPE.GST_INCLUDED) return true;
    return false;
  }

  function isParentOrderGstExcluded() {
    if (parentOrderDetails?.gst_type === ORDER_GST_TYPE.GST_EXCLUDED) return true;
    return false;
  }

  const getItemTotalForChildOrder = () => {
    return parentOrderDetails?.items?.reduce((acc, curr) => {
      if (curr.item_status === 1) acc = acc + curr.amount;

      return acc;
    }, 0);
  };

  /**
   * return the order process status text
   * @return {string}
   */
  const getParentOrderStatusProcessText = () => {
    const processingText = {
      title: '',
      subtitle: '',
    };

    if (
      parentOrderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
      parentOrderDetails?.status === ORDER_STATUS.CONFIRMED_COD
    ) {
      processingText.title = 'Order Placed';
      processingText.subtitle = 'We have received your order.';
    } else if (
      parentOrderDetails?.status === ORDER_STATUS.ORDER_SEEN &&
      parentOrderDetails?.digital_receipt === ''
    ) {
      processingText.title = 'Order Seen';
      processingText.subtitle = 'We will get your order ready for pickup soon.';
    } else {
      if (orderDetails?.order_type == ORDER_TYPE.PICKUP) {
        processingText.title = 'Your Order is Ready for Pickup';
      } else
        processingText.title = isB2bTypeOrder
          ? 'quotation has been Verified successfully'
          : 'Your Order is Verified Successfully';
    }

    return processingText;
  };

  const _renderParentOrderStatusComplete = () => {
    return (
      <>
        {parentOrderDetails?.status === ORDER_STATUS.COMPLETED ? (
          <article className="tc fw6 flex justify-start items-center">
            {parentOrderDetails?.prepaid_flag !== 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
            Your Order is Complete.
            {parentOrderDetails?.prepaid_flag === 1 && (
              <img
                src="https://cdn.dotpe.in/longtail/themes/8073865/q1kVDpRv.webp"
                className="img h2 mr2"
              />
            )}
          </article>
        ) : parentOrderDetails?.status === ORDER_STATUS.REJECTED &&
          parentOrderDetails?.display_status === DISPLAY_STATUS.REJECTED ? (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/eJFFlBiC.webp"
              className="mb2 img h2"
            />
            <br></br>
            Your order has been rejected
            <br></br>
            {parentOrderDetails?.status_message && (
              <span className="f7 fw6 black-40">
                {parentOrderDetails?.status_message}
              </span>
            )}
          </article>
        ) : (
          <article className="tc fw6">
            <img
              src="https://cdn.dotpe.in/longtail/themes/8073865/KIE24hYi.webp"
              className="mb2 img h2"
            />
            <br></br>
            {getParentOrderStatusProcessText()?.title}
            <br></br>
            {parentOrderDetails?.order_type === ORDER_TYPE.DELIVERY &&
              state.deliveryTracking.text && (
                <span className="f7 fw6 black-40">{state.deliveryTracking.text}</span>
              )}
          </article>
        )}
      </>
    );
  };

  /**
   * Return JSX for
   * Order Status Processing
   * @return {JSX}
   */
  const _renderParentOrderStatusProccessing = () => {
    return (
      <section className="order-status__processing">
        <div className="order-status__processing--top">
          <article className="title-text">
            <span className="f5 fw6">{getParentOrderStatusProcessText()?.title}</span>
            <br />
            <span className="f7 fw5 gray">
              {getParentOrderStatusProcessText()?.subtitle}
            </span>
          </article>
          <article className="status">
            <div
              className="f7 fw6 pv2 mr2 flex justify-start items-center pointer"
              onClick={toggleStatusBlock}
            >
              Check Status{' '}
              <img
                alt="dropdown button"
                src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
                className={`h2 img ml2 ${
                  state.isStatusBlockVisible ? 'arrow-up' : 'arrow-down'
                }`}
              />
            </div>
          </article>
        </div>
        {state.isStatusBlockVisible && (
          <div className="order-status__processing--bottom pt4 pb3 ph1 flex justify-between">
            <article className="flex flex-column items-center f7 fw6 mw4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              Order Placed
            </article>
            <article className="flex flex-column justify-center items-center tc f7 fw6 mw4 h-100">
              {parentOrderDetails?.status === ORDER_STATUS.ORDER_SEEN ? (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              ) : (
                <img
                  loading="lazy"
                  src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                  alt="tick"
                  className="h2 img z-2"
                />
              )}
              <br />
              Order Seen
              {(parentOrderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
                parentOrderDetails?.status === ORDER_STATUS.CONFIRMED_COD) && (
                <span className="o-30 f8 mt2">Order is yet to be seen.</span>
              )}
            </article>
            <article className="flex flex-column items-center  f7 fw6 m4 h-100">
              <img
                loading="lazy"
                src="https://cdn.dotpe.in/longtail/themes/8073865/quONXE2Z.webp"
                alt="tick"
                className="h2 img z-2"
              />
              <br />
              {parentOrderDetails?.order_type === ORDER_TYPE.PICKUP ? (
                <>
                  Ready for <br />
                  Pickup{' '}
                </>
              ) : (
                <>
                  Out for <br /> Delivery
                </>
              )}
            </article>
          </div>
        )}
      </section>
    );
  };

  const _renderOrderStatusForParentOrder = () => {
    return (
      <div className="order-status">
        {!(
          parentOrderDetails?.status === ORDER_STATUS.ORDER_SEEN ||
          parentOrderDetails?.status === ORDER_STATUS.ORDER_PLACED ||
          parentOrderDetails?.status === ORDER_STATUS.CONFIRMED_COD
        )
          ? _renderParentOrderStatusComplete()
          : _renderParentOrderStatusProccessing()}
      </div>
    );
  };

  const _renderBillForChildOrder = () => {
    return (
      <section className="order-bill">
        <div className="items-details-container">
          {parentOrderDetails?.items?.map((item, ind) => {
            const savingAmt = item?.actual_amount * item?.quantity - item.amount;
            return item.item_status === 1 ? (
              <article className="bill-table bill-items-margin" key={`order-item-${ind}`}>
                <div className="fw4 item-name-container responsive-major-text">
                  <div className="flex flex-column">
                    {item?.item_name} X {item?.quantity}
                    {item?.variant_name && (
                      <span className="mt4px star-dust responsive-minor-text">
                        {' '}
                        {item?.variant_name}{' '}
                      </span>
                    )}
                  </div>
                  {item.additional_remark ? (
                    <div className="flex flex-column f12px">
                      <p className="mt3 mb0 fw4 tile-gray-color">Additional Remarks:</p>
                      <p className="mt1 fw4 mb0">"{item.additional_remark}"</p>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-column fw4">
                  <span className="flex justify-end">
                    {item?.item_type !== 'list' ? `₹ ${item?.discounted_price}` : '-'}
                  </span>
                  {item?.item_type !== 'list' && savingAmt > 0 && (
                    <span className="fw4 mt4px color-green responsive-minor-text">
                      {CURRENCY_SYMBOL}&nbsp;{savingAmt?.toFixed(2)} Saved
                    </span>
                  )}
                </div>
              </article>
            ) : null;
          })}
        </div>
        <div className="overall-details-wrapper sonic-silver">
          <article className="bill-table">
            <div className="fw4 f7">
              {parentOrderDetails?.order_type === ORDER_TYPE.SERVICE
                ? 'Total'
                : 'Item Total'}
            </div>
            <div className="fw4 f7 tr">
              ₹&nbsp;{getItemTotalForChildOrder()?.toFixed(2)}
            </div>
          </article>
          {parentOrderDetails?.order_type === ORDER_TYPE.DELIVERY &&
            Number(parentOrderDetails?.delivery_charge) > 0 && (
              <article className="bill-table mt10px">
                <div className="fw4 f7">Delivery fees</div>
                <div className="fw4 f7 tr">
                  ₹&nbsp;
                  {Number(parentOrderDetails?.delivery_charge)}
                </div>
              </article>
            )}
          {parentOrderDetails?.order_type === ORDER_TYPE.DELIVERY &&
            Number(parentOrderDetails?.cod_charges) > 0 && (
              <article className="bill-table mt10px">
                <div className="fw4 f7">Extra Charges on COD</div>
                <div className="fw4 f7 tr">
                  ₹&nbsp;
                  {Number(parentOrderDetails?.cod_charges)}
                </div>
              </article>
            )}
          {parentOrderDetails?.extra_charges > 0 && (
            <article className="bill-table mt10px">
              <div className="fw4 f7">
                {parentOrderDetails?.extra_charge_name.substr(0, 20)}
                {parentOrderDetails?.extra_charge_name.length > 20 && '...'}
              </div>
              <div className="fw4 f7 tr">₹&nbsp;{parentOrderDetails?.extra_charges}</div>
            </article>
          )}
          {parentOrderDetails?.discount > 0 && (
            <article className="bill-table mt10px color-green">
              <div className="fw4 f7">Discount</div>
              <div className="fw4 f7 tr">- ₹&nbsp;{parentOrderDetails?.discount}</div>
            </article>
          )}
          {parentOrderDetails?.promo_discount > 0 && (
            <article className="bill-table mt10px color-green">
              <div className="fw4 f7">Promo Discount</div>
              <div className="fw4 f7 tr">
                - ₹&nbsp;{parentOrderDetails?.promo_discount}
              </div>
            </article>
          )}
          {isParentOrderGstExcluded() && (
            <article className="bill-table mt10px">
              <div className="fw4 f7">GST</div>
              <div className="fw4 f7 tr">₹&nbsp;{parentOrderDetails?.total_gst_paid}</div>
            </article>
          )}
        </div>
        <div className="conclusion">
          <article className="flex justify-between items-center">
            <span className="fw6 responsive-major-text">
              Grand Total
              {isParentOrderGstIncluded() && (
                <span className="incl-gst responsive-minor-text fw4 ml6px">
                  (Incl. ₹ {parentOrderDetails?.total_gst_paid} GST)
                </span>
              )}
            </span>
            <span className="fw6 pay-amount responsive-major-text">
              ₹&nbsp;
              {parentOrderDetails?.part_payment_info?.total_payable_amount}
            </span>
          </article>
          {parentOrderDetails?.offer_text ? (
            <div
              className="flex justify-start items-center f7 fw4 pv1 mr5"
              dangerouslySetInnerHTML={{
                __html: parentOrderDetails?.offer_text,
              }}
            />
          ) : null}
        </div>
      </section>
    );
  };

  const isThisCfeSideParentOrder = () => {
    return [1, 2].includes(parentOrderDetails?.order_type);
  };

  const _renderParentOrderStatusforAppSideOrders = () => {
    return !isOrderPending(parentOrderDetails?.display_status) &&
      parentOrderDetails?.display_status !== DISPLAY_STATUS.REJECTED ? (
      <div className="order-status">
        <article className="tc fw6 mb2">
          <img
            src="https://cdn.dotpe.in/longtail/themes/8073865/7pzKCEUO.webp"
            className="mb2 img h3"
          />
          <br></br>
          Your have paid ₹{parentOrderDetails.pay_amount}
        </article>
      </div>
    ) : null;
  };

  function renderDownloadInvoiceForParentOrder() {
    if (parentOrderDetails?.part_payment_info?.order_completion_status !== 3) {
      return null;
    }
    const parentGstInvoice = parentOrderDetails?.gst_invoice;
    return parentGstInvoice ? (
      <DownloadInvoiceAnchor
        href={parentOrderDetails?.gst_invoice}
        target="_blank"
        download="invoice.pdf"
      >
        <DownloadInvoiceContainer
          roundness={roundnessCalculator(storeInfo?.theme?.roundness)}
        >
          <DownloadInvoiceIcon />
          <DownloadInvoiceText>Download Invoice</DownloadInvoiceText>
        </DownloadInvoiceContainer>
      </DownloadInvoiceAnchor>
    ) : null;
  }

  function renderOrderDetailComponentWhenChildOrder() {
    return (
      <>
        {parentOrderDetails?.delivery_info?.tracking_url && (
          <section className="flex pa20px">
            <article className="w-60">
              <p className="mb1 f20px mt0">Arriving Soon</p>
              <div className="f12px lh-copy">Track your Shipment now</div>
            </article>
            <article className="flex justify-end items-start w-40">
              <a
                className="pv10px ph24px link"
                style={{
                  color: storeInfo?.theme?.colors?.primary_color
                    ? storeInfo?.theme?.colors?.primary_color
                    : '#ea5151',
                  border: `1px solid ${
                    storeInfo?.theme?.colors?.primary_color
                      ? storeInfo?.theme?.colors?.primary_color
                      : '#ea5151'
                  }`,
                  borderRadius: roundnessCalculator(storeInfo?.theme?.roundness),
                }}
                href={parentOrderDetails?.delivery_info?.tracking_url}
                rel="noreferrer"
                target="_blank"
              >
                Track
              </a>
            </article>
          </section>
        )}
        {/* ORDER STATUS SECTION  */}
        {isThisCfeSideParentOrder()
          ? _renderOrderStatusForParentOrder()
          : _renderParentOrderStatusforAppSideOrders()}
        {parentOrderDetails?.part_payment_info &&
        parentOrderDetails?.part_payment_info?.order_completion_status !==
          3 ? null : !isOrderPending(parentOrderDetails?.display_status) &&
          reviewSettings?.status &&
          isCRRFeatureUnlocked &&
          user?.isLogin ? (
          <div
            className="flex items-center justify-between pv16px ph16px pointer"
            onClick={() =>
              document.getElementById('review-section')?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              })
            }
          >
            <p className="mv0 fw5 f14px">Rate the products</p>
            <img
              alt="dropdown button"
              src="https://cdn.dotpe.in/longtail/themes/8073865/igP6Hh58.webp"
              width="10px"
            />
          </div>
        ) : null}
        {parentOrderDetails?.order_type === ORDER_TYPE.SERVICE && (
          <section className="bb b--dashed b--black-20 pa3">
            <h4 className="f14px ma0 mt2">Booked Successfully</h4>
            <p className="f12px ma0 mt1 mb4" style={{ color: '#777777' }}>
              We have received your booking
            </p>
          </section>
        )}
        {orderDetails?.prepaid_flag === 0 &&
          isOrderPending(orderDetails?.display_status) && (
            <section className="bb b--dashed b--black-20 pa3">
              <article>
                {isExtraCODCHargeFeatureActive() && (
                  <button
                    className={`pv3 f5 fw6 white bn w-100`}
                    style={{
                      backgroundColor: storeInfo?.theme?.colors?.primary_color
                        ? storeInfo?.theme?.colors?.primary_color
                        : '#ea5151',
                      borderRadius: roundnessCalculator(storeInfo?.theme?.roundness),
                    }}
                    onClick={onPayNowBtnClick}
                  >
                    Pay Now
                  </button>
                )}
                {isPaymentTypeIsCOD() && (
                  <p
                    className="mb0 mt2 tc f12px fw6"
                    style={{
                      color: storeInfo?.theme?.colors?.primary_color
                        ? storeInfo?.theme?.colors?.primary_color
                        : '#ea5151',
                    }}
                  >
                    Pay ₹{orderDetails?.pay_amount} to delivery person
                  </p>
                )}
              </article>
              {isOrderInInitialisedState() && (
                <p className="f7 fw5 black-50">
                  {parentOrderDetails?.order_type === ORDER_TYPE.SERVICE
                    ? 'You can make a payment once you avail the service.'
                    : parentOrderDetails?.order_type === ORDER_TYPE.PICKUP
                      ? 'You can make payment once order is ready for pickup.'
                      : 'You can make payment once order is Verified Successfully.'}
                </p>
              )}
            </section>
          )}
        {orderDetails?.status !== ORDER_STATUS.REJECTED &&
          orderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          renderDownloadInvoiceForParentOrder()}
        <div className="bill-wrapper">
          <div className="bill-wrapper__orderDetails flex justify-center items-center">
            <article className="fw6 f16px flex flex-column">
              <span className="flex justify-center">
                {getCustomCtaText('bill_order_id')} {parentOrderDetails?.order_id}
              </span>
              {renderOrderTime()}
            </article>
          </div>
        </div>
        {/* DIGITAL BILL  */}
        {_renderBillForChildOrder()}
        {!!storeInfo?.services?.partial_payment_flag &&
          parentOrderDetails.part_payment_info &&
          _renderPartialPaymentDetail(parentOrderDetails)}
        {parentOrderDetails?.part_payment_info &&
        parentOrderDetails?.part_payment_info?.order_completion_status !==
          3 ? null : !isOrderPending(parentOrderDetails?.display_status) &&
          reviewSettings?.status &&
          isCRRFeatureUnlocked &&
          parentOrderDetails?.status !== ORDER_STATUS.REJECTED &&
          parentOrderDetails?.status !== ORDER_STATUS.CUSTOMER_CANCELLED &&
          user?.isLogin ? (
          <div id="review-section">
            <ReviewOrderItems
              triggerGetOrderByHash={triggerGetOrderByHash}
              items={parentOrderDetails?.items}
              orderDetails={parentOrderDetails}
            />
          </div>
        ) : null}
      </>
    );
  }

  /** @return {boolean}  Returns a boolean depending on whether the order status page is for a normal order or a child order sent via bill generated  */
  function isOrderChildPartialPaymentType() {
    return (
      orderDetails?.parent_order_id !== 0 &&
      orderDetails?.parent_order_hash !== '' &&
      parentOrderDetails &&
      parentOrderDetails?.order_id === orderDetails?.parent_order_id
    );
  }

  return (
    <section className="premium-order-status-page">
      {deviceWidth > MAX_MOBILE_WIDTH &&
        specificThemeRender(storeInfo)?.components?.Header?.map((layout) => {
          return getThemeComponent(layout.sub_type, layout.name, layout);
        })}
      {thankYouHeader()}
      {/* 
         If loader is active then render loader component
         if order_status is not 10 then render main bill segment
         otherwise render no order exists
       */}
      <main className="pb6">
        {loader ? (
          <div className="pa3">
            <OrderStatusLoader />
          </div>
        ) : isOrderChildPartialPaymentType() ? (
          renderOrderDetailComponentWhenChildOrder()
        ) : orderDetails?.status !== ORDER_STATUS.PAYMENT_PENDING || isB2bTypeOrder ? (
          orderDetailComponent()
        ) : (
          noOrderDetailsFound()
        )}
        {isB2bTypeOrder && renderB2bRfqDetails()}
      </main>
      {footerComponent()}
      {/* For Desktop only */}
      {deviceWidth > 990 && <DesktopPaymentModal />}
      {showAuthModal && (
        <AuthModal
          toggleModal={toggleAuthModal}
          isVisible={showAuthModal}
          callback={() => window.location.reload()}
        />
      )}
    </section>
  );
};

export default OrderStatus;
